import React, {
  ReactElement,
  useEffect,
  useState,
  useCallback,
  useMemo,
} from 'react';
import { Responsive, Layouts, ResponsiveProps } from 'react-grid-layout';
import { useDispatch, useSelector } from 'react-redux';
import { useMount } from 'react-use';

import { LSHelper } from '@crm-framework/utils';

import { Panel, IPanelProps } from './Panel';
import {
  getIsEditModeOn,
  getIsPanelResetting,
  gridLayoutActions,
  getCurrentBreakpoint,
  getVisiblePanelsList,
  getIsLoading,
} from './store';
import WidthProvider from './WidthProvider';
import { breakpoints, cols, LayoutActionType } from './consts';
import {
  checkIsOpenLayouts,
  getLayouts,
  getLayoutsWithMinSizes,
  getVisiblePanelList,
  resetLayouts,
  saveLayouts,
} from './utils';
import { IStateWithGridLayout, ILayoutMinSizes } from './models';

import 'react-grid-layout/css/styles.css';
import 'react-resizable/css/styles.css';
import './styles.css';

const ResponsiveReactGridLayout = WidthProvider(Responsive);

export type PanelItem = IPanelProps & {
  isHidden?: boolean;
};

type GridLayoutType = ResponsiveProps & {
  storageName: string;
  defaultLayout: Layouts;
  contentItems: PanelItem[];
  withCollapsibleContent?: boolean;
  layoutsMinSizes?: ILayoutMinSizes;
  withPanelsMenu?: boolean;
  hasCollapsedHeader?: boolean;
};

export const GridResponsiveLayout: React.VFC<GridLayoutType> = ({
  storageName,
  defaultLayout,
  layoutsMinSizes,
  contentItems,
  withCollapsibleContent = true,
  withPanelsMenu = false,
  hasCollapsedHeader = true,
  ...props
}) => {
  const dispatch = useDispatch();
  const visiblePanelsList = useSelector((state: IStateWithGridLayout) =>
    getVisiblePanelsList(state, storageName)
  );
  const panelStorageName = `${storageName}-panels`;
  const [layoutsState, setLayoutsState] = useState(defaultLayout);
  const isPanelResetting = useSelector((state: IStateWithGridLayout) =>
    getIsPanelResetting(state, storageName)
  );
  const isEditModeOn = useSelector((state: IStateWithGridLayout) =>
    getIsEditModeOn(state, storageName)
  );
  const breakPoint = useSelector(getCurrentBreakpoint);
  const isLoading = useSelector(getIsLoading);
  const setCurrentBreakpoint = useCallback((newBreakpoint: string) => {
    dispatch(gridLayoutActions.setCurrentBreakpoint(newBreakpoint));
  }, []);

  useEffect(() => {
    updateLocalLayout(true);
    dispatch(gridLayoutActions.setIsLoading(false));
  }, [visiblePanelsList]);

  useMount(() => {
    setTimeout(() => {
      setLayoutsState(
        getVisiblePanelList({
          layout: getLayouts({
            save: true,
            storageName,
            defaultLayout,
            layoutsMinSizes,
            panelStorageName,
          }),
          visiblePanelsList,
          withPanelsMenu,
        })
      );
    }, 100);
  });

  const updateLocalLayout = useCallback(
    (save: boolean = false) => {
      const newLayout = getLayouts({
        save,
        storageName,
        defaultLayout,
        layoutsMinSizes,
        panelStorageName,
      });
      setLayoutsState(
        getVisiblePanelList({
          layout: newLayout,
          visiblePanelsList,
          withPanelsMenu,
        })
      );
    },
    [
      storageName,
      defaultLayout,
      layoutsMinSizes,
      panelStorageName,
      visiblePanelsList,
    ]
  );

  useEffect(() => {
    if (isPanelResetting) {
      const resetLayout = resetLayouts({
        storageName,
        defaultLayout,
        layoutsMinSizes,
        panelStorageName,
      });
      setLayoutsState(
        getVisiblePanelList({
          layout: resetLayout,
          visiblePanelsList,
          withPanelsMenu,
        })
      );
      dispatch(
        gridLayoutActions.resetPanels({ name: storageName, value: false })
      );
    }
  }, [isPanelResetting]);

  const updateLayout = (layout, _oldItem, newItem, type: LayoutActionType) => {
    const localLayouts = LSHelper.getObject(storageName);

    if (localLayouts) {
      const newState = {
        ...localLayouts,
        [breakPoint]: localLayouts[breakPoint].map((elem) => {
          if (elem.i === newItem.i && type === LayoutActionType.resize)
            return { ...newItem, h: newItem.h > 3 ? newItem.h : 3 };

          const layoutItem = layout.find((item) => item.i === elem.i);

          return layoutItem
            ? { ...elem, x: layoutItem.x, y: layoutItem.y }
            : elem;
        }),
      };
      saveLayouts({ newLayout: newState, storageName });
      const checkedLayout = checkIsOpenLayouts(
        getLayoutsWithMinSizes(newState, layoutsMinSizes),
        panelStorageName
      );
      setLayoutsState(
        getVisiblePanelList({
          layout: checkedLayout,
          visiblePanelsList,
          withPanelsMenu,
        })
      );
    }
  };

  const visibleContentItems = useMemo(
    () =>
      (withPanelsMenu
        ? contentItems.reduce(
            (itemsList, item) =>
              visiblePanelsList?.includes(item.panelKey)
                ? [...itemsList, item]
                : itemsList,
            []
          )
        : contentItems
      ).filter((item) => !item.isHidden),
    [visiblePanelsList, contentItems]
  );

  if (isPanelResetting || isLoading) {
    return null;
  }

  return (
    <ResponsiveReactGridLayout
      className={storageName}
      isBounded
      autoSize
      onBreakpointChange={(newBreakpoint) =>
        setCurrentBreakpoint(newBreakpoint)
      }
      cols={cols}
      useCSSTransforms={false}
      onResizeStop={(layout, oldItem, newItem) =>
        updateLayout(layout, oldItem, newItem, LayoutActionType.resize)
      }
      onDragStop={(layout, oldItem, newItem) =>
        updateLayout(layout, oldItem, newItem, LayoutActionType.drag)
      }
      layouts={layoutsState}
      breakpoints={breakpoints}
      {...props}
      isDraggable={isEditModeOn}
      isResizable={isEditModeOn}>
      {visibleContentItems.map(
        ({ content, panelKey, ...panelProps }): ReactElement => (
          <Panel
            key={panelKey}
            panelKey={panelKey}
            {...panelProps}
            className={panelKey}
            panelStorageName={panelStorageName}
            updateLayout={updateLocalLayout}
            isCollapsible={withCollapsibleContent}
            hasCollapsedHeader={hasCollapsedHeader && !isEditModeOn}>
            {content}
          </Panel>
        )
      )}
    </ResponsiveReactGridLayout>
  );
};
